"use client"
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from "react"
import type { PublicPage } from "../content/content-types.ts"
import styles from "./authenticated-content-dialog.module.css"
import { Icon } from "./icons.tsx"
import { useManagedDialog } from "./use-managed-dialog.ts"

type ContentDialogContextValue = {
  readonly openDialog: (trigger: HTMLButtonElement) => void
  readonly slug: string
}
type AuthenticatedContentDialogProps = {
  readonly children: ReactNode
  readonly page: PublicPage
}
type AuthenticatedContentDialogTriggerProps = {
  readonly children: ReactNode
  readonly className?: string
}
const ContentDialogContext = createContext<ContentDialogContextValue | null>(null)
const useContentDialog = (): ContentDialogContextValue => {
  const context = useContext(ContentDialogContext)
  if (context === null) throw new TypeError("CONTENT_DIALOG_TRIGGER_REQUIRES_PROVIDER")
  return context
}
export const AuthenticatedContentDialogTrigger = ({ children, className }: AuthenticatedContentDialogTriggerProps) => {
  const { openDialog, slug } = useContentDialog()
  return (
    <button className={className} data-content-dialog-trigger={slug} onClick={(event) => openDialog(event.currentTarget)} type="button">
      {children}
    </button>
  )
}
export const AuthenticatedContentDialog = ({ children, page }: AuthenticatedContentDialogProps) => {
  const isGuide = page.slug === "guide"
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const titleId = `authenticated-content-${page.slug}-title`
  const restoreTriggerFocus = useCallback((): void => {
    setOpen(false)
    triggerRef.current?.focus()
    triggerRef.current = null
  }, [])
  const { cancel, dialogRef, onDialogClose } = useManagedDialog(open, restoreTriggerFocus)
  const openDialog = (trigger: HTMLButtonElement): void => {
    triggerRef.current = trigger
    setOpen(true)
  }
  return (
    <ContentDialogContext value={{ openDialog, slug: page.slug }}>
      {children}
      <dialog
        aria-labelledby={titleId}
        className={styles["contentDialog"]}
        data-content-dialog={page.slug}
        data-guide-sheet={isGuide || undefined}
        onCancel={(event) => {
          event.preventDefault()
          cancel()
        }}
        onClose={onDialogClose}
        ref={dialogRef}
      >
        <header className={styles["contentDialogHeader"]}>
          {isGuide ? null : (
            <span aria-hidden="true" className={styles["contentDialogHeaderIcon"]}>
              <Icon name="balance" />
            </span>
          )}
          <div>
            <h2 className={styles["contentDialogTitle"]} id={titleId}>
              {page.title}
            </h2>
            {isGuide ? null : <p className={styles["contentDialogEyebrow"]}>{page.eyebrow}</p>}
          </div>
          <button aria-label="닫기" className={styles["contentDialogClose"]} data-content-dialog-close onClick={cancel} type="button">
            {isGuide ? "닫기" : <span aria-hidden="true">×</span>}
          </button>
        </header>
        <section aria-label={`${page.title} 본문`} className={styles["contentDialogScroll"]} data-content-dialog-scroll>
          <p className={styles["contentDialogSummary"]}>{page.summary}</p>
          <ol className={styles["contentDialogSteps"]}>
            {page.sections.map((section) => (
              <li data-content-dialog-step key={section.title}>
                <section>
                  <h3>{section.title}</h3>
                  <p>{section.body}</p>
                  {section.items === undefined ? null : (
                    <ul>
                      {section.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                </section>
              </li>
            ))}
          </ol>
        </section>
      </dialog>
    </ContentDialogContext>
  )
}
